import { useEffect, useMemo, useState } from "react";
import { FaEdit, FaPlus, FaUserCheck, FaUserSlash } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";
import UserAuditLogsModal from "../components/UserAuditLogsModal";
import UserFormModal from "../components/UserFormModal";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("bg-BG", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
}

function formatDateTime(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";

  return date.toLocaleString("bg-BG", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

const roleBadgeClassByCode = {
  PM: "text-bg-success",
  CM: "text-bg-primary",
  RQM: "text-bg-warning",
  DES: "text-bg-info",
  IMP: "text-bg-dark",
  TST: "text-bg-danger",
  VER: "text-bg-secondary",
  VAL: "text-bg-light text-dark border",
  ASR: "text-bg-light text-dark border",
  USR: "text-bg-light text-dark border"
};

function getRoleBadgeClass(roleCode) {
  return roleBadgeClassByCode[roleCode] ?? "text-bg-secondary";
}

export default function Users({ user }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [auditLogUser, setAuditLogUser] = useState(null);
  const [error, setError] = useState("");

  const isPm = Boolean(user?.roles?.includes("PM") || user?.isAdmin);
  const canManageUsers = Number(user?.id) === 1;

  const loadUsers = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        fetchWithAuth("/api/users"),
        fetchWithAuth("/api/users/roles")
      ]);

      if (!usersRes.ok || !rolesRes.ok) {
        throw new Error("Failed to load users");
      }

      setUsers(await usersRes.json());
      setRoles(await rolesRes.json());
      setError("");
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (isPm) {
      loadUsers();
    }
  }, [isPm]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;

    return users.filter((item) => {
      const roleText = (item.roles ?? []).map((role) => `${role.code} ${role.name}`).join(" ");

      return (
        String(item.name ?? "").toLowerCase().includes(query) ||
        String(item.email ?? "").toLowerCase().includes(query) ||
        String(item.lastLoginAt ?? "").toLowerCase().includes(query) ||
        String(item.mustChangePassword ? "must change password temporary password" : "").toLowerCase().includes(query) ||
        roleText.toLowerCase().includes(query)
      );
    });
  }, [users, search]);

  const setUserActive = async (targetUser, isActive) => {
    if (!canManageUsers) {
      return;
    }

    const action = isActive ? "activate" : "deactivate";

    if (!window.confirm(`Are you sure you want to ${action} ${targetUser.name}?`)) {
      return;
    }

    const res = await fetchWithAuth(`/api/users/${targetUser.id}/active`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isActive })
    });

    if (!res.ok) {
      const responseError = await res.json().catch(() => null);
      alert(responseError?.error || "Status update failed");
      return;
    }

    loadUsers();
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (item, index) => {
    if (item.isActive === false) {
      return { "--bs-table-bg": "rgba(108, 117, 125, 0.12)" };
    }

    return {
      "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
    };
  };

  if (!isPm) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          Users management is available only for PM role.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="mb-1">Users</h3>
          <div className="text-muted">
            {canManageUsers
              ? "Manage application users, roles and active access."
              : "View application users, roles and active access."}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
            <input
              type="text"
              className="form-control pe-5"
              placeholder="Search users..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </div>

          <span className="badge text-bg-secondary">
            Total: {filteredUsers.length}
          </span>

          {canManageUsers && (
            <button
              type="button"
              className="btn btn-success"
              onClick={() => {
                setEditingUser(null);
                setShowForm(true);
              }}
            >
              <FaPlus className="me-1" />
              Add user
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2">{error}</div>
      )}

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ ...stickyHeaderStyle, width: "220px" }}>Name</th>
              <th style={{ ...stickyHeaderStyle, width: "260px" }}>Email</th>
              <th style={stickyHeaderStyle}>Roles</th>
              <th className="text-center" style={{ ...stickyHeaderStyle, width: "120px" }}>Status</th>
              <th style={{ ...stickyHeaderStyle, width: "170px" }}>Last login</th>
              <th style={{ ...stickyHeaderStyle, width: "130px" }}>Created</th>
              {canManageUsers && (
                <th style={{ ...stickyHeaderStyle, width: "140px" }}>Actions</th>
              )}
            </tr>
          </thead>

          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={canManageUsers ? 7 : 6} className="text-center text-muted py-4">
                  No users found.
                </td>
              </tr>
            ) : filteredUsers.map((item, index) => (
              <tr key={item.id} style={getRowStyle(item, index)}>
                <td className="fw-semibold">
                  <button
                    type="button"
                    className="btn btn-link p-0 fw-semibold text-decoration-none"
                    onClick={() => setAuditLogUser(item)}
                    title="Show audit logs"
                  >
                    {item.name}
                  </button>
                </td>
                <td>{item.email || <span className="text-muted">-</span>}</td>
                <td>
                  <div className="d-flex flex-wrap gap-1">
                    {(item.roles ?? []).map((role) => (
                      <span
                        key={role.code}
                        className={`badge ${getRoleBadgeClass(role.code)}`}
                        title={role.name}
                      >
                        {role.code}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="text-center">
                  {item.isActive === false ? (
                    <span className="badge text-bg-secondary">Inactive</span>
                  ) : (
                    <span className="badge text-bg-success">Active</span>
                  )}
                  {item.mustChangePassword && (
                    <span className="badge text-bg-warning ms-1">Password</span>
                  )}
                </td>
                <td>{formatDateTime(item.lastLoginAt)}</td>
                <td>{formatDate(item.createdAt)}</td>
                {canManageUsers && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => {
                        setEditingUser(item);
                        setShowForm(true);
                      }}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>

                    {item.isActive === false ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        onClick={() => setUserActive(item, true)}
                        title="Activate"
                      >
                        <FaUserCheck />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => setUserActive(item, false)}
                        disabled={item.id === user?.id}
                        title={item.id === user?.id ? "You cannot deactivate yourself" : "Deactivate"}
                      >
                        <FaUserSlash />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canManageUsers && (
        <UserFormModal
          show={showForm}
          onClose={() => setShowForm(false)}
          onSaved={loadUsers}
          user={editingUser}
          roles={roles}
        />
      )}

      <UserAuditLogsModal
        show={Boolean(auditLogUser)}
        onClose={() => setAuditLogUser(null)}
        user={auditLogUser}
      />
    </div>
  );
}
